import { AuthShell, Button, Field, Notice } from '@concord/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { ME_KEY, safeNext, useMe, useSignOut } from '../../lib/session';
import s from './auth.module.css';
import { AUTH_IMAGES } from './auth-image';

/**
 * The second step when a staff member signs in with the emailed code (or link): the code the
 * authenticator app shows now. Staff need both (George, Oct 2, 2026).
 */
export function SecondStep() {
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'), '/console');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const signOut = useSignOut();
  const { data: me, isPending } = useMe();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isPending) return null;
  if (me?.realm !== 'staff')
    return <Navigate to={`/console/sign-in?next=${encodeURIComponent(next)}`} replace />;
  if (!me.authenticator?.enabled || me.authenticator.verified) return <Navigate to={next} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      await api('/staff/authenticator/verify', { body: { code } });
      await qc.invalidateQueries({ queryKey: ME_KEY });
      navigate(next, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setCode('');
      setBusy(false);
    }
  }

  return (
    <AuthShell image={AUTH_IMAGES.staff}>
      <form className={s.card} onSubmit={submit} noValidate>
        <span className={s.realm}>Care navigator console</span>
        <h1>One more step</h1>
        <p className={s.lead}>Enter the 6-digit code your authenticator app shows now.</p>
        <Field label="Code from your authenticator app" error={error}>
          {(id, describedBy) => (
            <input
              id={id}
              className="input code-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              aria-describedby={describedBy}
              aria-invalid={error ? true : undefined}
            />
          )}
        </Field>
        <Button type="submit" size="lg" busy={busy} disabled={code.length !== 6}>
          Continue
        </Button>
        <p className={s.fine}>
          Lost or replaced your phone? Ask an admin to remove the app from your account; you then set it up
          again after signing in.
        </p>
        {error?.startsWith('Too many') && <Notice>Wait a few minutes before trying again.</Notice>}
        <button type="button" className={s.linkBtn} onClick={() => signOut('/console/sign-in')}>
          Sign out
        </button>
      </form>
    </AuthShell>
  );
}
